import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { apiRequest } from '../../utils/apiClient';
import { GooeyLoader } from '../../components/ui/gooey-loader';
import ResultCard from '../search/components/ResultCard';
import { toFrontendProperty, type SearchProperty } from '../../services/ragSearchService';
import type { Property } from '../../mocks/properties';
import { PROJECT_STATUS_LABELS, investmentLabel, type DeveloperProject } from './DeveloperProjectCard';

interface ProjectDeveloper {
  id: string;
  name: string;
  nameAr?: string | null;
  slug: string;
  logoUrl?: string | null;
  isVerified?: boolean;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
}

interface ProjectDetail extends DeveloperProject {
  address?: string | null;
  developer: ProjectDeveloper;
}

interface ListMeta { totalItems: number; totalPages: number }

export default function DeveloperProjectPage() {
  const { i18n } = useTranslation(['common']);
  const isRTL = i18n.dir() === 'rtl';
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [units, setUnits] = useState<Property[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);

  useEffect(() => {
    if (!projectSlug) return;
    setLoading(true);
    setNotFound(false);
    apiRequest<{ data: ProjectDetail }>(`/developers/projects/${projectSlug}`)
      .then((res) => setProject(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [projectSlug]);

  const fetchUnits = useCallback(async (projectId: string) => {
    setUnitsLoading(true);
    try {
      const res = await apiRequest<{ data: SearchProperty[]; meta?: ListMeta }>(
        `/properties?developerProjectId=${projectId}&page=1&pageSize=48`,
      );
      setUnits((res.data ?? []).map(toFrontendProperty) as Property[]);
    } catch {
      setUnits([]);
    } finally {
      setUnitsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (project?.id) fetchUnits(project.id);
  }, [project?.id, fetchUnits]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white" dir={i18n.dir()}>
        <Navigation />
        <div className="flex items-center justify-center py-32">
          <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !project) {
    return (
      <div className="min-h-screen bg-white" dir={i18n.dir()}>
        <Navigation />
        <div className="max-w-xl mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] mb-4">{isRTL ? 'المشروع غير موجود' : 'Project not found'}</h1>
          <Link to="/trusted-brokers"><Button variant="primary">{isRTL ? 'تصفح المطورين' : 'Browse developers'}</Button></Link>
        </div>
        <Footer />
      </div>
    );
  }

  const name = isRTL ? (project.nameAr || project.name) : project.name;
  const description = isRTL ? (project.descriptionAr || project.description) : project.description;
  const location = isRTL ? (project.region || project.city) : (project.city || project.region);
  const devName = isRTL ? (project.developer.nameAr || project.developer.name) : project.developer.name;
  const st = PROJECT_STATUS_LABELS[project.status];
  const invest = investmentLabel(project.investmentStatus, isRTL);

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)]" dir={i18n.dir()}>
      <Navigation />
      <main className="pb-20">
        {/* Hero */}
        <div
          className="h-56 sm:h-72 w-full bg-[#004D40] bg-cover bg-center"
          style={project.coverUrl ? { backgroundImage: `url(${project.coverUrl})` } : undefined}
        />
        <div className="max-w-6xl mx-auto px-4">
          <section className={`-mt-16 rounded-3xl bg-white border border-slate-200/80 shadow-sm p-5 sm:p-7 mb-6 ${isRTL ? 'text-right' : ''}`}>
            <div className={`flex flex-wrap items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${st.cls}`}>{isRTL ? st.ar : st.en}</span>
              {invest && <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-[11px] font-medium text-indigo-700">{invest}</span>}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--pv-color-primary)]">{name}</h1>
            <div className={`mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {location && <span className="flex items-center gap-1"><i className="ri-map-pin-2-line" />{location}</span>}
              {typeof project.totalUnits === 'number' && (
                <span className="flex items-center gap-1"><i className="ri-home-4-line" />{project.totalUnits} {isRTL ? 'وحدة' : 'units'}</span>
              )}
            </div>

            {/* Developer attribution */}
            <Link
              to={`/developers/${project.developer.slug}`}
              className={`mt-4 inline-flex items-center gap-2.5 rounded-2xl border border-slate-200 px-3 py-2 hover:bg-slate-50 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              {project.developer.logoUrl ? (
                <img src={project.developer.logoUrl} alt={devName} className="h-9 w-9 rounded-lg object-cover bg-white" />
              ) : (
                <div className="h-9 w-9 rounded-lg bg-[#004D40] flex items-center justify-center text-white text-sm font-bold">{devName.charAt(0)}</div>
              )}
              <span className="text-sm">
                <span className="block text-[11px] text-slate-400">{isRTL ? 'المطوّر' : 'Developer'}</span>
                <span className="font-medium text-slate-800">{devName}{project.developer.isVerified && <i className="ri-verified-badge-fill text-[#004D40] ms-1" />}</span>
              </span>
            </Link>

            {description && <p className={`mt-4 text-gray-600 leading-relaxed ${isRTL ? 'text-right' : ''}`}>{description}</p>}

            {/* Invest CTA */}
            {project.investmentStatus === 'OPEN_FOR_INVESTMENT' && (
              <Link
                to="/investors"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#004D40] px-5 py-2.5 text-sm font-medium text-white hover:opacity-90 transition-opacity"
              >
                <i className="ri-funds-line" />{isRTL ? 'مهتم بالاستثمار' : 'Interested in investing'}
              </Link>
            )}
          </section>

          {/* Units — "the listing + the current listings below it" */}
          <h2 className={`text-xl font-bold text-[var(--pv-color-primary)] mb-4 ${isRTL ? 'text-right' : ''}`}>{isRTL ? 'الوحدات المتاحة' : 'Available units'}</h2>
          {unitsLoading ? (
            <div className="flex justify-center py-12"><GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" /></div>
          ) : units.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-2xl border border-gray-200 text-gray-500">
              {isRTL ? 'لا توجد وحدات متاحة حالياً' : 'No units available yet'}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {units.map((p, index) => (
                <ResultCard key={p.id} property={p} eagerImage={index < 3} onSelect={(item) => navigate(`/property/${item.id}`)} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
